import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ChevronDown, Trash2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import GuestPortalFooter from "../components/GuestPortalFooter";
import { setAgentThemeColor } from "../utils/agentTheme";
import { StepProgressIndicator } from "../components/StepProgressIndicator";
import { API, BACKEND_URL } from "../config/api";


// Helper to resolve image URLs (handles both absolute URLs and relative paths)
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
  if (url.startsWith('/api/uploads/')) return `${BACKEND_URL}${url}`;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

const categoryConfig = {
  pre_arrival: { label: "Pre-Arrival", order: 1 },
  upgrades: { label: "Concierge Upgrades", order: 2 },
  on_island: { label: "In Destination", order: 3 },
  private_chefs: { label: "Private Chefs", order: 3.5 },
  experiences: { label: "Experiences", order: 4 },
  yacht_charter: { label: "Yacht Charter", order: 5 }
};

export default function SummaryPage() {
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [agentContext, setAgentContext] = useState(null);
  const [services, setServices] = useState([]);
  const [yachts, setYachts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [guestNotes, setGuestNotes] = useState("");
  const [expandedSections, setExpandedSections] = useState({
    pre_arrival: true,
    upgrades: true,
    on_island: true
  });

  useEffect(() => {
    const loadData = async () => {
      const bookingData = sessionStorage.getItem("bookingData");
      if (!bookingData) {
        navigate("/");
        return;
      }

      const parsed = JSON.parse(bookingData);
      setBooking(parsed);
      
      // Load agent context
      const agentData = sessionStorage.getItem("agentContext");
      if (agentData) {
        const parsedAgent = JSON.parse(agentData);
        setAgentContext(parsedAgent);
        // Set agent theme color
        setAgentThemeColor(parsedAgent.button_color || parsed.button_color);
      } else {
        // Fallback to booking data for button color
        setAgentThemeColor(parsed.button_color);
      }

      try {
        const [servicesRes, yachtsRes] = await Promise.all([
          axios.get(`${API}/services`, { params: { destination: parsed.destination } }),
          axios.get(`${API}/yachts`, { params: { destination: parsed.destination } }).catch(() => ({ data: { yachts: [] } }))
        ]);
        setServices(servicesRes.data);
        setYachts(yachtsRes.data?.yachts || []);
      } catch (error) {
        console.error("Failed to load services");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [navigate]);

  const getServiceDetails = (serviceId) => services.find(s => s.id === serviceId);

  // Helper to get image URL from selection or service - resolves relative URLs
  const getSelectionImageUrl = (selection) => {
    // First check if image_url is stored directly in selection
    if (selection.image_url) {
      return getImageUrl(selection.image_url);
    }
    
    // For experiences - check various image fields
    if (selection.type === 'experience' && selection.experience_id) {
      const imgUrl = selection.image_url || selection.images?.[0];
      return imgUrl ? getImageUrl(imgUrl) : '/placeholder-experience.jpg';
    }
    
    // For yachts - match by yacht_id, service_id, or yacht_name from answers
    if (selection.type === 'yacht' && selection.yacht_id) {
      const imgUrl = selection.image_url || selection.yacht_image;
      return imgUrl ? getImageUrl(imgUrl) : '/placeholder-yacht.jpg';
    }
    
    // Check if this is a yacht selection by service_name pattern or answers.yacht_name
    const yachtName = selection.answers?.yacht_name;
    if (yachtName || selection.service_name?.startsWith('Yacht Charter:')) {
      // Try matching by service_id first (yacht ID)
      let yacht = yachts.find(y => y.id === selection.service_id);
      // Fallback: match by yacht_name in answers
      if (!yacht && yachtName) {
        yacht = yachts.find(y => y.name === yachtName);
      }
      if (yacht) {
        const imgUrl = yacht.hero_image || yacht.images?.[0];
        if (imgUrl) return getImageUrl(imgUrl);
      }
    }
    
    // For regular services, look up from services list
    const service = getServiceDetails(selection.service_id);
    if (service?.image_url) {
      return getImageUrl(service.image_url);
    }
    
    // Default fallback
    return '/placeholder-service.jpg';
  };

  const groupSelectionsByCategory = () => {
    const grouped = {};
    booking?.selections?.forEach((selection) => {
      let category;
      
      // Handle experiences
      if (selection.type === 'experience') {
        category = 'experiences';
      }
      // Handle yachts (explicit type or detected by name/answers)
      else if (selection.type === 'yacht' || selection.category === 'yacht_charter' || selection.answers?.yacht_name || selection.service_name?.startsWith('Yacht Charter:')) {
        category = 'yacht_charter';
      }
      // Handle regular services
      else {
        const service = getServiceDetails(selection.service_id);
        category = service?.category || selection.category || "on_island";
        // Map destination-specific experience categories to the main experiences group
        if (category.startsWith('experiences_')) {
          category = 'experiences';
        }
      }
      
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push({ ...selection, service: getServiceDetails(selection.service_id) });
    });
    return grouped;
  };

  const handleRemoveSelection = async (selectionId) => {
    try {
      await axios.delete(`${API}/bookings/${booking.id}/selections/${selectionId}`);
      
      const updatedSelections = booking.selections.filter(s => s.id !== selectionId);
      const updatedBooking = { ...booking, selections: updatedSelections };
      setBooking(updatedBooking);
      sessionStorage.setItem("bookingData", JSON.stringify(updatedBooking));
      
      toast.success("Removed from plan");
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const handleSendPlan = async () => {
    if (isSending || booking?.plan_sent) return;
    
    if (booking.selections.length === 0) {
      toast.error("Please add at least one service");
      return;
    }

    // Save guest notes to booking, then navigate to card capture
    setIsSending(true);
    try {
      if (guestNotes) {
        await axios.post(`${API}/bookings/${booking.id}/send-plan`, {
          guest_notes: guestNotes,
          skip_notifications: true
        });
      }
      const updatedBooking = { ...booking, guest_notes: guestNotes };
      sessionStorage.setItem("bookingData", JSON.stringify(updatedBooking));
      navigate("/preferences");
    } catch {
      // Even if saving notes fails, let them proceed
      const updatedBooking = { ...booking, guest_notes: guestNotes };
      sessionStorage.setItem("bookingData", JSON.stringify(updatedBooking));
      navigate("/preferences");
    }
  };

  const toggleSection = (category) => {
    setExpandedSections(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const formatAnswerValue = (value, questionType) => {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return value;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const groupedSelections = groupSelectionsByCategory();
  const sortedCategories = Object.keys(groupedSelections).sort(
    (a, b) => (categoryConfig[a]?.order || 99) - (categoryConfig[b]?.order || 99)
  );
  
  const agentName = agentContext?.agent_name || booking?.agent_name || "Charles Marlow";
  
  // Get full logo URL
  const getLogoUrl = () => {
    const logoUrl = agentContext?.logo_url || booking?.logo_url;
    if (!logoUrl) return null;
    if (logoUrl.startsWith('/')) {
      return `${BACKEND_URL}${logoUrl}`;
    }
    return logoUrl;
  };

  return (
    <div className="min-h-screen bg-background pb-44">
      {/* Header with Logo */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm">
        <div className="container-luxury py-5 flex items-center justify-between">
          <button
            onClick={() => navigate("/services")}
            data-testid="back-button"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          
          {/* Agent Logo or Name */}
          {getLogoUrl() ? (
            <img 
              src={getLogoUrl()} 
              alt={agentName}
              className="h-8 object-contain"
              style={{ maxWidth: '140px' }}
            />
          ) : (
            <span className="brand-logo brand-logo-small">{agentName}</span>
          )}
          
          <div className="w-4" />
        </div>

        {/* Progress indicator */}
        <div className="container-luxury pb-5">
          <StepProgressIndicator currentStep={3} />
        </div>
      </div>

      {/* Content */}
      <div className="container-luxury pt-36">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="heading-section mb-4">Your Plan</h1>
          <p className="body-text mb-6">
            Review your selections before sending to our concierge team.
          </p>
          
          {/* Guest Summary */}
          {(booking?.num_adults || booking?.num_children > 0) && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-10" data-testid="guest-summary">
              <span>Guests:</span>
              <span className="font-medium text-foreground">
                {booking.num_adults} {booking.num_adults === 1 ? 'adult' : 'adults'}
                {booking.num_children > 0 && `, ${booking.num_children} ${booking.num_children === 1 ? 'child' : 'children'}`}
              </span>
            </div>
          )}

          {/* Empty state */}
          {booking?.selections?.length === 0 ? (
            <div className="text-center py-20">
              <p className="body-text mb-8">You haven't added any services yet.</p>
              <button
                onClick={() => navigate("/services")}
                className="btn-secondary max-w-xs mx-auto"
              >
                Browse services
              </button>
            </div>
          ) : (
            /* Itinerary sections */
            <div className="space-y-10">
              {sortedCategories.map((category) => {
                const items = groupedSelections[category];
                const config = categoryConfig[category];
                const isExpanded = expandedSections[category];

                return (
                  <div key={category} className="itinerary-section">
                    <button
                      onClick={() => toggleSection(category)}
                      className="w-full itinerary-header"
                    >
                      <div>
                        <h2 className="itinerary-title">{config?.label || category}</h2>
                        <p className="itinerary-count">{items.length} {items.length === 1 ? "service" : "services"}</p>
                      </div>
                      <ChevronDown 
                        className={`w-5 h-5 text-muted-foreground transition-transform ${
                          isExpanded ? "rotate-180" : ""
                        }`} 
                      />
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {items.map((item) => (
                            <div
                              key={item.id}
                              data-testid={`selection-${item.id}`}
                              className="itinerary-item"
                            >
                              {/* Image or Package Icon */}
                              {item.service?.category === 'upgrades' || item.category === 'upgrades' ? (
                                <div className="w-20 h-20 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a1b23, #2a2b33)' }}>
                                  <Star className="w-8 h-8" style={{ color: '#D4AF37', fill: '#D4AF37' }} />
                                </div>
                              ) : (
                                <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-secondary flex items-center justify-center">
                                  <img
                                    src={getSelectionImageUrl(item)}
                                    alt={item.service_name || item.experience_name || item.yacht_name || 'Service'}
                                    className="w-full h-full object-cover"
                                    style={{ filter: "brightness(0.97) saturate(0.92)" }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const parent = e.target.parentElement;
                                      parent.innerHTML = '';
                                      parent.style.background = 'linear-gradient(135deg, #1a1b23, #2a2b33)';
                                      parent.style.display = 'flex';
                                      parent.style.alignItems = 'center';
                                      parent.style.justifyContent = 'center';
                                      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                                      svg.setAttribute('width', '32');
                                      svg.setAttribute('height', '32');
                                      svg.setAttribute('viewBox', '0 0 24 24');
                                      svg.setAttribute('fill', '#D4AF37');
                                      svg.setAttribute('stroke', '#D4AF37');
                                      svg.setAttribute('stroke-width', '2');
                                      svg.innerHTML = '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>';
                                      parent.appendChild(svg);
                                    }}
                                  />
                                </div>
                              )}

                              {/* Details */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-4">
                                  <div>
                                    <h3 className="text-foreground font-medium mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.0625rem" }}>
                                      {item.service_name || item.experience_name || item.yacht_name || 'Service'}
                                    </h3>
                                    <p className="text-sm text-primary font-medium">
                                      {item.service?.price_display}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => handleRemoveSelection(item.id)}
                                    data-testid={`remove-${item.id}`}
                                    className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>

                                {/* Answers summary */}
                                {item.answers && Object.keys(item.answers).length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-border/30 space-y-1">
                                    {Object.entries(item.answers)
                                      .filter(([k, v]) => v && v !== "" && v !== false && k !== "add_ons" && k !== "yacht_name" && k !== "from_price")
                                      .slice(0, 4)
                                      .map(([key, value]) => {
                                        const question = item.service?.questions?.find(q => q.id === key);
                                        return (
                                          <p key={key} className="text-sm text-muted-foreground">
                                            {question?.label || key}: {formatAnswerValue(value, question?.type)}
                                          </p>
                                        );
                                      })}
                                  </div>
                                )}

                                {/* Add-ons */}
                                {item.answers?.add_ons && item.answers.add_ons.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-border/30">
                                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Add-ons</p>
                                    <div className="space-y-1">
                                      {item.answers.add_ons.map((addon, aidx) => (
                                        <p key={aidx} className="text-sm text-muted-foreground">
                                          {addon.name}{addon.pricing ? ` — ${addon.pricing}` : ""}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>

      {/* Guest Notes Section - Above Submit */}
      <div className="container-luxury pb-48">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card rounded-xl p-6 border border-border/50"
        >
          <label className="block font-medium text-foreground mb-2">
            Anything else you'd like us to know?
          </label>
          <p className="text-sm text-muted-foreground mb-4">
            Please include any additional requests, preferences, or special arrangements.
          </p>
          <textarea
            value={guestNotes}
            onChange={(e) => setGuestNotes(e.target.value)}
            data-testid="guest-notes-input"
            placeholder="E.g., celebrating a birthday, dietary restrictions, preferred timing for activities..."
            rows={4}
            className="form-input w-full resize-none"
          />
        </motion.div>
      </div>

      {/* Fixed bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-sm border-t border-border/40">
        <div className="container-luxury py-6">
          <button
            onClick={handleSendPlan}
            data-testid="send-plan-btn"
            disabled={isSending || booking?.plan_sent || booking?.selections?.length === 0}
            className="btn-agent-primary mb-4"
          >
            {isSending ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Sending...</span>
              </span>
            ) : booking?.plan_sent ? (
              "Request Sent ✓"
            ) : (
              "Send My Request"
            )}
          </button>
          <p className="disclaimer text-center">
            Final availability and pricing will be confirmed before booking.
          </p>
        </div>
      </div>
      
      {/* Dynamic Legal Footer */}
      <GuestPortalFooter 
        poweredByBrand={agentContext?.powered_by_brand || booking?.powered_by_brand || "my_concierge_portal"}
        privacyPolicyUrl={agentContext?.privacy_policy_url || booking?.privacy_policy_url || "https://myworldvip.com/privacy-policy"}
        termsConditionsUrl={agentContext?.terms_conditions_url || booking?.terms_conditions_url || "https://myworldvip.com/terms-and-conditions"}
      />
    </div>
  );
}
